import { Component, OnInit } from '@angular/core'
import { AdminService, DeletedPostsFilter } from '../../services/admin.service'
import { HttpStatusCode } from '@angular/common/http'
import { MatDialog } from '@angular/material/dialog'
import { CreateNewSaleComponent } from '../../dialogs/create-new-sale/create-new-sale.component'
import { CreateNewAccommodationComponent } from '../../dialogs/create-new-accommodation/create-new-accommodation.component'
import { FormControl } from '@angular/forms'
import { forkJoin } from 'rxjs'
import { CallerService } from '../../services/caller.service'
import { AdminPostDetailsDialogComponent } from '../../dialogs/admin-post-details-dialog/admin-post-details-dialog.component'

/** Same as corpers.online's DELETED_POST_RETENTION_DAYS (services/deletedPostPurgeWorker.js). */
const DELETED_POST_RETENTION_DAYS = 14

@Component({
  selector: 'app-admin-posts',
  templateUrl: './admin-posts.component.html',
  styleUrls: ['./admin-posts.component.css'],
})
export class AdminPostsComponent implements OnInit {
  sales: any[] = []
  accommodations: any[] = []
  loading = false
  selectedTabIndex = 0
  creatingSale = false
  creatingAccommodation = false
  searchControl = new FormControl('', [])
  stateCodeControl = new FormControl('', [])
  stateControl = new FormControl<number | string | ''>('', [])
  lgaControl = new FormControl<number | string | ''>('', [])
  deletedControl = new FormControl<DeletedPostsFilter>('hide', { nonNullable: true })
  stateOptions: Array<{ id: number | string; name: string }> = []
  lgaOptions: Array<{ id: number | string; name: string }> = []

  constructor(
    public adminService: AdminService,
    private dialog: MatDialog,
    private callerService: CallerService
  ) {}

  ngOnInit(): void {
    this.loadStateOptions()
    this.loadItems()
    this.searchControl.valueChanges.subscribe(() => this.loadItems())
    this.stateCodeControl.valueChanges.subscribe(() => this.loadItems())
    this.stateControl.valueChanges.subscribe((stateId) => {
      this.lgaControl.setValue('', { emitEvent: false })
      this.loadLgaOptions(stateId)
      this.loadItems()
    })
    this.lgaControl.valueChanges.subscribe(() => this.loadItems())
    this.deletedControl.valueChanges.subscribe(() => this.loadItems())
  }

  /** When a post its poster deleted is removed for good (the backend purges it 14 days on). */
  purgeDate(post: { deleted_at?: string | null }): Date | null {
    return post.deleted_at ? new Date(new Date(post.deleted_at).getTime() + DELETED_POST_RETENTION_DAYS * 24 * 60 * 60 * 1000) : null
  }

  private loadStateOptions(): void {
    this.callerService.fetchAllNigeriaStates().subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.stateOptions = (res.body?.states ?? []).map((s: any) => ({ id: s.id, name: s.name }))
        }
      },
      error: () => {
        this.adminService.showNotification('Failed to load states', 4000, 'Close')
      },
    })
  }

  private loadLgaOptions(stateId: number | string | '' | null | undefined): void {
    if (!stateId) {
      this.lgaOptions = []
      return
    }

    const numericStateId = Number(stateId)
    if (!Number.isFinite(numericStateId)) {
      this.lgaOptions = []
      return
    }

    this.callerService.fetchNigeriaStateLGAs(numericStateId).subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.lgaOptions = (res.body?.lgas ?? []).map((lga: any) => ({ id: lga.id, name: lga.name }))
        }
      },
      error: () => {
        this.lgaOptions = []
        this.adminService.showNotification('Failed to load LGAs', 4000, 'Close')
      },
    })
  }

  private getSelectedStateName(): string | undefined {
    const selectedStateId = this.stateControl.value
    if (!selectedStateId) return undefined
    return this.stateOptions.find((s) => String(s.id) === String(selectedStateId))?.name
  }

  loadItems(): void {
    this.loading = true
    const search = this.searchControl.value?.trim() || undefined
    const state_code = this.stateCodeControl.value?.trim() || undefined
    const state = this.getSelectedStateName()
    const lga_id = this.lgaControl.value ? String(this.lgaControl.value) : undefined
    const params = { page: 1, limit: 50, search, state_code, state, lga_id, deleted: this.deletedControl.value }

    forkJoin({
      salesRes: this.adminService.getSalePosts(params),
      accommodationsRes: this.adminService.getAccommodationPosts(params),
    }).subscribe({
      next: ({ salesRes, accommodationsRes }) => {
        this.loading = false
        this.sales =
          salesRes.status === HttpStatusCode.Ok && salesRes.body
            ? salesRes.body.sales ?? salesRes.body.data ?? (Array.isArray(salesRes.body) ? salesRes.body : [])
            : []
        this.accommodations =
          accommodationsRes.status === HttpStatusCode.Ok && accommodationsRes.body
            ? accommodationsRes.body.accommodations ??
              accommodationsRes.body.data ??
              (Array.isArray(accommodationsRes.body) ? accommodationsRes.body : [])
            : []
      },
      error: () => {
        this.loading = false
        this.adminService.showNotification('Failed to load posts', 5000, 'Close')
      },
    })
  }

  deleteSale(sale: any): void {
    const id = sale.id ?? sale._id
    if (!id) return
    if (!confirm(sale.deleted_at ? 'Its poster deleted it. Remove it and its images for good now?' : 'Delete this sale post and its images?')) return
    this.adminService.deleteSalePost(id).subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.adminService.showNotification('Sale deleted', 3000, 'OK')
          this.loadItems()
        }
      },
      error: (err) => {
        this.adminService.showNotification(err?.error?.message ?? 'Failed to delete', 5000, 'Close')
      },
    })
  }

  deleteAccommodation(acc: any): void {
    const id = acc.id ?? acc._id
    if (!id) return
    if (!confirm(acc.deleted_at ? 'Its poster deleted it. Remove it and its images for good now?' : 'Delete this accommodation post and its images?')) return
    this.adminService.deleteAccommodationPost(id).subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.adminService.showNotification('Accommodation deleted', 3000, 'OK')
          this.loadItems()
        }
      },
      error: (err) => {
        this.adminService.showNotification(err?.error?.message ?? 'Failed to delete', 5000, 'Close')
      },
    })
  }

  openCreateSale(): void {
    this.creatingSale = true
    const ref = this.dialog.open(CreateNewSaleComponent, {
      width: '90vw',
      maxWidth: '600px',
      data: { isAdmin: true },
    })
    ref.afterClosed().subscribe((created) => {
      this.creatingSale = false
      if (created) this.loadItems()
    })
  }

  openCreateAccommodation(): void {
    this.creatingAccommodation = true
    const ref = this.dialog.open(CreateNewAccommodationComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100dvw',
      maxHeight: '100dvh',
      data: { isAdmin: true },
    })
    ref.afterClosed().subscribe((created) => {
      this.creatingAccommodation = false
      if (created) this.loadItems()
    })
  }

  openPostDetails(post: any, type: 'sale' | 'accommodation'): void {
    this.dialog.open(AdminPostDetailsDialogComponent, {
      width: 'min(960px, 95vw)',
      maxWidth: '95vw',
      data: {
        type,
        post,
        media: this.getPostMedia(post),
      },
    })
  }

  getPostMedia(post: any): Array<{ url: string; alt_text?: string }> {
    if (!post || typeof post !== 'object') return []

    const rawMedia = post.Media ?? post.media ?? post.images ?? []
    if (!Array.isArray(rawMedia)) return []

    return rawMedia
      .map((media: any) => {
        if (typeof media === 'string') {
          return { url: media }
        }

        const url = media?.url ?? media?.secure_url ?? media?.image_url ?? media?.src
        if (!url || typeof url !== 'string') return null

        return {
          url,
          alt_text: typeof media?.alt_text === 'string' ? media.alt_text : undefined,
        }
      })
      .filter((media): media is { url: string; alt_text?: string } => !!media)
  }

  handleImageError(event: Event): void {
    const target = event.target as HTMLImageElement | null
    if (!target) return
    target.src = 'https://placehold.co/120x120?text=No+Image'
  }
}
